/**
 * Cookie Consent Provider
 * Context provider that wraps the app and provides cookie consent state
 */

import { createContext, useContext, type ReactNode } from 'react';
import type { CookieConsentContextValue } from '../types';
import { useCookieConsent } from '../hooks/useCookieConsent';
import { CONSENT_MANAGED_BY_GTM } from '../constants';
import { CookieBanner } from './CookieBanner';
import { CookieDialog } from './CookieDialog';

/** Context for cookie consent */
const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

/** Hook to access cookie consent context */
export function useCookieConsentContext(): CookieConsentContextValue {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsentContext must be used within CookieConsentProvider');
  }
  return context;
}

interface CookieConsentProviderProps {
  children: ReactNode;
}

/**
 * Provider component that manages cookie consent state and renders UI
 */
export function CookieConsentProvider({ children }: CookieConsentProviderProps) {
  const consent = useCookieConsent();

  return (
    <CookieConsentContext.Provider value={consent}>
      {children}

      {/* When consent is managed by the GTM-loaded production CMP
          (cookie_set_prod_v20.js), our in-house banner/dialog must NOT render —
          otherwise the user sees two cookie UIs and we'd write a second,
          incompatible consent cookie. The context above stays available so
          existing consumers keep working. */}
      {!CONSENT_MANAGED_BY_GTM && (
        <>
          {/* Cookie Banner - shown on first visit */}
          <CookieBanner
            visible={consent.showBanner}
            onOpenSettings={consent.openDialog}
            onAcceptAll={consent.acceptAll}
          />

          {/* Cookie Dialog - full settings */}
          <CookieDialog
            open={consent.showDialog}
            onClose={consent.closeDialog}
            preferences={consent.preferences}
            onUpdatePreference={consent.updatePreference}
            onAcceptAll={consent.acceptAll}
            onAcceptSelected={consent.acceptSelected}
          />
        </>
      )}
    </CookieConsentContext.Provider>
  );
}
